import React from 'react';
import { Stack, Typography } from '@mui/material';
import { useLocales } from 'src/locales';
import { useNavigate } from 'react-router-dom';
import PulseDot from 'src/components/pulse-dot';
import { useResponsiveConfig } from 'src/components/competitive-intelligence/utils/use-responsive-config';
import HoverShortcut from 'src/components/competitive-intelligence/components/HoverShortcut';
import countriesJson from 'src/utils/countries.json';
import { dashboardPaths } from 'src/routes/dashboardPaths';

type CompetitionEvolutionLevel = 'high' | 'active' | 'low' | string;

type Props = {
  level: CompetitionEvolutionLevel | null;
  brandDomain?: string | null;
  country?: string | null;
  competitorDomain?: string | null;
  brandPresenceRate?: number | null;
  competitorPresenceRate?: number | null;
  isDemoBrand?: boolean;
  fullWidth?: boolean;
};


export default function CompetitionEvolutionCell({ level, brandDomain, country }: Props) {
  const { t } = useLocales();
  const navigate = useNavigate();
  const responsiveConfig = useResponsiveConfig();
  // TODO: remove this, for development purposes
  if (level === null) {
    level = 'high';
  }

  // Color mapping: high -> red, active -> orange, low -> green
  const colorByLevel = React.useMemo(() => {
    if (level === 'high') return "#E34B4E";
    if (level === 'active') return "#FFA444";
    return "#14C779"; // low -> green
  }, [level]);

  const dotSize = 16;

  // Cette fonction traduit les 3 niveaux de concurrence en français
  const mapLevelToLabelTranslated = (level: CompetitionEvolutionLevel | null) => {
    switch (level) {
      case 'high':
        return t('dashboard.competitiveIntelligence.competitionEvolutionLevels.high');
      case 'active':
        return t('dashboard.competitiveIntelligence.competitionEvolutionLevels.active');
      case 'low':
        return t('dashboard.competitiveIntelligence.competitionEvolutionLevels.low');
      default:
        return level ?? '';
    }
  };

  // Link to Ads page filtered by region (country) and domain
  const handleBrandShortcut = React.useCallback((e: React.MouseEvent) => {
    if (!brandDomain) return;
    const countryEntry = countriesJson?.find((c: any) => c.name === country);
    const code = countryEntry?.code?.toUpperCase() || '';
    const url = `${dashboardPaths.competitiveAds}?country=${encodeURIComponent(code)}&domain=${encodeURIComponent(brandDomain)}`;
    navigate(url);
  }, [brandDomain, country]);

  const displayBrand = "see ads";

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', minWidth: 0 }}>
      <Typography variant="body2" sx={{ minWidth: responsiveConfig.competitionEvolutionLabelMinWidth, flexShrink: 0 }}>
        {mapLevelToLabelTranslated(level)}
      </Typography>
      <PulseDot color={colorByLevel} size={dotSize} />
      {brandDomain ? (
        <HoverShortcut
          defaultContent={displayBrand}
          hoverContent={displayBrand}
          onClick={handleBrandShortcut}
          fontSize={responsiveConfig.fontSize.body}
          sx={{ ml: 0, minWidth: 0, overflow: 'hidden' }}
        />
      ) : null}
    </Stack>
  );
}

